import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfig } from 'hooks/use-config';
import {
  LIBRARY_FOLDERS_PAGE_SIZE,
  createLibraryFolder,
  deleteLibraryFolder,
  getLibraryFolders,
  listLibraryFolders,
  mapLibraryFolderToFileData,
  moveLibraryFile,
  renameLibraryFolder,
  type CreateLibraryFolderPayload,
  type MoveLibraryFilePayload,
  type RenameLibraryFolderPayload,
} from '../services/library-folders-service';

export const LIBRARY_FOLDERS_QUERY_KEY = ['library-folders'];

const invalidateLibraryQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: LIBRARY_FOLDERS_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: ['media-files'] });
};

export const useGetLibraryFolders = (enabled = true) => {
  const { configurationSettings } = useConfig();

  return useQuery({
    queryKey: LIBRARY_FOLDERS_QUERY_KEY,
    queryFn: async () => {
      const folders = await getLibraryFolders(configurationSettings);
      return folders.map(mapLibraryFolderToFileData);
    },
    enabled,
  });
};

export const useListLibraryFoldersInfinite = (
  payload: { search?: string; pageSize?: number } = {},
  queryOptions?: { enabled?: boolean }
) => {
  const { configurationSettings } = useConfig();
  const pageSize = payload.pageSize ?? LIBRARY_FOLDERS_PAGE_SIZE;
  const search = payload.search?.trim() || undefined;

  return useInfiniteQuery({
    queryKey: [...LIBRARY_FOLDERS_QUERY_KEY, 'list', { pageSize, search }],
    queryFn: ({ pageParam = 1 }) =>
      listLibraryFolders(configurationSettings, {
        page: pageParam,
        pageSize,
        search,
        sortBy: 'fileName',
        order: 'asc',
      }),
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.data.length, 0);
      if (totalFetched < lastPage.totalCount) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: queryOptions?.enabled ?? true,
  });
};

export const useCreateLibraryFolder = () => {
  const { configurationSettings } = useConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateLibraryFolderPayload) =>
      createLibraryFolder(configurationSettings, payload),
    onSuccess: () => {
      invalidateLibraryQueries(queryClient);
    },
  });
};

export const useRenameLibraryFolder = () => {
  const { configurationSettings } = useConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RenameLibraryFolderPayload) =>
      renameLibraryFolder(configurationSettings, payload),
    onSuccess: () => {
      invalidateLibraryQueries(queryClient);
    },
  });
};

export const useDeleteLibraryFolder = () => {
  const { configurationSettings } = useConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fileId: string) => deleteLibraryFolder(configurationSettings, fileId),
    onSuccess: () => {
      // Breadcrumbs only. The file list is refreshed after the page is back on
      // /media-library, so this does not request the deleted folder.
      queryClient.invalidateQueries({ queryKey: LIBRARY_FOLDERS_QUERY_KEY });
    },
  });
};

export const useMoveLibraryFile = () => {
  const { configurationSettings } = useConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: MoveLibraryFilePayload) =>
      moveLibraryFile(configurationSettings, payload),
    onSuccess: () => {
      invalidateLibraryQueries(queryClient);
    },
  });
};
